import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import StatusPill from "@/components/StatusPill";
import FileAttachments from "@/components/FileAttachments";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, ClipboardList, Search, ShieldAlert, ClipboardCheck,
  Lock, History, Trash2, RotateCcw, CheckCircle2, Plus, Save, MoreVertical
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TABS = [
  { key: "details", label: "Incident Details", icon: ClipboardList },
  { key: "investigation", label: "Investigation", icon: Search },
  { key: "actions", label: "Actions", icon: ClipboardCheck },
  { key: "safeguarding", label: "Safeguarding", icon: ShieldAlert },
  { key: "closure", label: "Review & Closure", icon: Lock },
  { key: "audit", label: "Audit Trail", icon: History },
];

const STATUSES = ["reported", "manager_review", "under_investigation", "actions_required", "awaiting_closure", "closed"];
const CATEGORIES = ["accident", "safeguarding", "near_miss", "behaviour", "injury", "fall", "medication", "other"];
const SEVERITIES = ["low", "moderate", "high", "critical"];
const SAFEGUARDING = ["not_applicable", "yes", "no", "under_review"];
const ACTION_STATUSES = ["not_started", "in_progress", "completed", "overdue"];
const ACTION_PRIORITIES = ["low", "medium", "high", "urgent"];
const DOC_STATUSES = ["yes", "no", "not_applicable"];

function humanize(v) { return v ? String(v).replace(/_/g, " ") : "—"; }
function fmt(v) { return v ? String(v).slice(0, 16).replace("T", " ") : "—"; }

export default function IncidentFull() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("details");
  const [inc, setInc] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  const canManage = ["admin", "manager", "registered_manager", "deputy_manager", "service_manager", "super_admin"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/incidents/${id}`);
      setInc(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadAudit = useCallback(async () => {
    try {
      const r = await api.get(`/incidents/${id}/audit`);
      setAudit(r.data.events || []);
    } catch (e) { /* silent */ }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "audit") loadAudit(); }, [tab, loadAudit]);

  if (loading || !inc) {
    return <div className="p-8 text-sm text-slate-500" data-testid="incident-loading">Loading incident…</div>;
  }

  const overdue = (inc.actions || []).some((a) => a.status === "overdue");
  const highSev = ["high", "critical"].includes(inc.severity);

  const handleReopen = async () => {
    try { await api.post(`/incidents/${id}/reopen`); toast.success("Incident reopened"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Desktop Header ( ≥ lg ) — unchanged from before Batch 5.1. */}
      <div className="hidden lg:block border-b border-slate-200 bg-white px-6 py-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/incidents")} className="text-slate-500 hover:text-slate-900" data-testid="btn-back-to-list">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Incident</div>
              <h1 className="font-heading text-base font-semibold text-slate-900" data-testid="incident-title">
                {inc.reference} · <span className="capitalize">{humanize(inc.category)}</span>
              </h1>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Occurred {fmt(inc.occurred_at)} · Reported by {inc.reported_by || inc.created_by_name}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill value={inc.severity} kind="severity" />
            <StatusPill value={inc.status} kind="generic" />
            {overdue && <span className="pill pill-danger">overdue action</span>}
            {inc.status === "closed" && canManage && (
              <Button size="sm" variant="outline" onClick={handleReopen} data-testid="btn-reopen"><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reopen</Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-3 flex gap-1 border-b border-slate-200 -mb-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`px-3 py-2 text-xs font-medium border-b-2 flex items-center gap-1.5 transition ${active ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Batch 5.1 · Mobile / tablet header ( <lg ). Back button + title
          on top; Close/Reopen intentionally moved into a kebab More menu
          to avoid accidental taps next to the navigation control. All
          existing permission gates, confirmations, validation and audit
          writes are preserved (same handlers as desktop). */}
      <div className="lg:hidden border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0">
        <div className="flex items-center gap-2 px-2 py-2">
          <button
            onClick={() => navigate("/incidents")}
            data-testid="mobile-btn-back-to-list"
            aria-label="Back to incidents"
            className="w-11 h-11 inline-flex items-center justify-center rounded-md text-slate-600 dark:text-slate-300 active:bg-slate-100 dark:active:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Incident</div>
            <div className="font-heading text-sm font-semibold text-slate-900 dark:text-slate-100 truncate" data-testid="mobile-incident-title">
              {inc.reference} · <span className="capitalize">{humanize(inc.category)}</span>
            </div>
          </div>
          {inc.status === "closed" && canManage && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="mobile-incident-more"
                  aria-label="More"
                  className="w-11 h-11 inline-flex items-center justify-center rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">Governance</DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleReopen} data-testid="mobile-btn-reopen">
                  <RotateCcw className="w-4 h-4 mr-2" /> Reopen incident
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div className="px-3 pb-2 flex items-center gap-1.5 flex-wrap">
          <StatusPill value={inc.severity} kind="severity" />
          <StatusPill value={inc.status} kind="generic" />
          {overdue && <span className="pill pill-danger">overdue action</span>}
        </div>
        <div className="px-1 flex gap-1 border-t border-slate-100 dark:border-slate-800 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }} data-testid="mobile-incident-tabs">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`shrink-0 min-h-[44px] px-3 text-xs font-medium border-b-2 flex items-center gap-1.5 transition ${active ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500"}`}
              >
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-slate-50 dark:bg-slate-950">
        <div className="max-w-5xl mx-auto p-3 lg:p-6">
          {tab === "details" && <DetailsTab inc={inc} canManage={canManage} onSaved={load} />}
          {tab === "investigation" && <InvestigationTab inc={inc} canManage={canManage} onSaved={load} />}
          {tab === "actions" && <ActionsTab inc={inc} canManage={canManage} onSaved={load} />}
          {tab === "safeguarding" && <SafeguardingTab inc={inc} canManage={canManage} onSaved={load} />}
          {tab === "closure" && <ClosureTab inc={inc} canManage={canManage} highSev={highSev} onSaved={load} />}
          {tab === "audit" && <AuditTab events={audit} />}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Details tab
// ---------------------------------------------------------------------------
function DetailsTab({ inc, canManage, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(inc);
  useEffect(() => setV(inc), [inc]);
  const patch = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const save = async () => {
    try {
      const payload = {
        occurred_at: v.occurred_at, category: v.category, severity: v.severity,
        client_name: v.client_name, location: v.location,
        reported_by: v.reported_by, witnesses: v.witnesses,
        description: v.description, immediate_action: v.immediate_action,
        injury_harm: v.injury_harm, medical_treatment_required: v.medical_treatment_required,
        emergency_services: v.emergency_services,
      };
      await api.patch(`/incidents/${inc.id}`, payload);
      toast.success("Incident updated");
      setEdit(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Section title="Incident details" right={canManage && !edit ? <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="btn-edit-details">Edit</Button> : null}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ROrE edit={edit} label="Reference" value={inc.reference} />
        <ROrE edit={edit} label="Date & Time" value={fmt(v.occurred_at)}>
          <Input type="datetime-local" value={v.occurred_at?.slice(0, 16) || ""} onChange={(e) => patch("occurred_at", e.target.value)} data-testid="edit-occurred-at" />
        </ROrE>
        <ROrE edit={edit} label="Category" value={humanize(v.category)}>
          <Select value={v.category} onChange={(x) => patch("category", x)} options={CATEGORIES} testid="edit-category" />
        </ROrE>
        <ROrE edit={edit} label="Severity" value={<StatusPill value={v.severity} kind="severity" />}>
          <Select value={v.severity} onChange={(x) => patch("severity", x)} options={SEVERITIES} testid="edit-severity" />
        </ROrE>
        <ROrE edit={edit} label="Service User" value={v.client_name || "—"}>
          <Input value={v.client_name || ""} onChange={(e) => patch("client_name", e.target.value)} data-testid="edit-client-name" />
        </ROrE>
        <ROrE edit={edit} label="Site / Location" value={v.location || "—"}>
          <Input value={v.location || ""} onChange={(e) => patch("location", e.target.value)} data-testid="edit-location" />
        </ROrE>
        <ROrE edit={edit} label="Reported by" value={v.reported_by || inc.created_by_name}>
          <Input value={v.reported_by || ""} onChange={(e) => patch("reported_by", e.target.value)} data-testid="edit-reported-by" />
        </ROrE>
        <ROrE edit={edit} label="Witnesses" value={(v.witnesses || []).join(", ") || "—"}>
          <Input value={(v.witnesses || []).join(", ")} onChange={(e) => patch("witnesses", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Comma separated" data-testid="edit-witnesses" />
        </ROrE>
        <div className="col-span-2">
          <ROrE edit={edit} label="Detailed description" value={<div className="whitespace-pre-wrap">{v.description || "—"}</div>}>
            <Textarea rows={4} value={v.description || ""} onChange={(e) => patch("description", e.target.value)} data-testid="edit-description" />
          </ROrE>
        </div>
        <div className="col-span-2">
          <ROrE edit={edit} label="Immediate action taken" value={<div className="whitespace-pre-wrap">{v.immediate_action || "—"}</div>}>
            <Textarea rows={3} value={v.immediate_action || ""} onChange={(e) => patch("immediate_action", e.target.value)} data-testid="edit-immediate-action" />
          </ROrE>
        </div>
        <div className="col-span-2">
          <ROrE edit={edit} label="Injury / harm" value={<div className="whitespace-pre-wrap">{v.injury_harm || "—"}</div>}>
            <Textarea rows={2} value={v.injury_harm || ""} onChange={(e) => patch("injury_harm", e.target.value)} data-testid="edit-injury-harm" />
          </ROrE>
        </div>
        <ROrE edit={edit} label="Medical treatment required" value={v.medical_treatment_required ? "Yes" : "No"}>
          <label className="flex items-center gap-2 text-sm text-slate-700 h-9">
            <input type="checkbox" checked={!!v.medical_treatment_required} onChange={(e) => patch("medical_treatment_required", e.target.checked)} data-testid="edit-medical-treatment" />
            Medical attention was needed
          </label>
        </ROrE>
        <ROrE edit={edit} label="Emergency services" value={(v.emergency_services || []).join(", ") || "—"}>
          <div className="flex flex-wrap gap-2 mt-2">
            {["ambulance", "police", "fire", "gp_out_of_hours"].map((o) => (
              <label key={o} className="flex items-center gap-1 text-xs text-slate-700 capitalize">
                <input
                  type="checkbox"
                  checked={(v.emergency_services || []).includes(o)}
                  onChange={() => patch("emergency_services", (v.emergency_services || []).includes(o) ? v.emergency_services.filter((x) => x !== o) : [...(v.emergency_services || []), o])}
                  data-testid={`edit-emergency-${o}`}
                />
                {o.replace(/_/g, " ")}
              </label>
            ))}
          </div>
        </ROrE>
      </div>

      {edit && (
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => { setEdit(false); setV(inc); }} data-testid="btn-cancel-edit">Cancel</Button>
          <Button onClick={save} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-save-details"><Save className="w-3.5 h-3.5 mr-1.5" /> Save</Button>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-slate-200">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Attachments &amp; evidence</div>
        <FileAttachments module="incidents" recordId={inc.id} />
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Investigation
// ---------------------------------------------------------------------------
function InvestigationTab({ inc, canManage, onSaved }) {
  const [v, setV] = useState(inc.investigation || {
    investigator: "", started_at: "", completed_at: "",
    what_happened: "", contributing_factors: "", root_cause: "",
    findings: "", evidence: "", lessons_identified: ""
  });
  const patch = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const save = async () => {
    try {
      await api.put(`/incidents/${inc.id}/investigation`, v);
      toast.success("Investigation saved");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Section title="Investigation">
      {!canManage && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">Read-only — only managers can update this section.</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FLabel label="Investigator"><Input value={v.investigator || ""} onChange={(e) => patch("investigator", e.target.value)} disabled={!canManage} data-testid="inv-investigator" /></FLabel>
        <FLabel label="Investigation start date"><Input type="date" value={v.started_at || ""} onChange={(e) => patch("started_at", e.target.value)} disabled={!canManage} data-testid="inv-started" /></FLabel>
        <FLabel label="Investigation completion date"><Input type="date" value={v.completed_at || ""} onChange={(e) => patch("completed_at", e.target.value)} disabled={!canManage} data-testid="inv-completed" /></FLabel>
        <div />
        <div className="col-span-2"><FLabel label="What happened?"><Textarea rows={3} value={v.what_happened || ""} onChange={(e) => patch("what_happened", e.target.value)} disabled={!canManage} data-testid="inv-what-happened" /></FLabel></div>
        <div className="col-span-2"><FLabel label="Contributing factors"><Textarea rows={3} value={v.contributing_factors || ""} onChange={(e) => patch("contributing_factors", e.target.value)} disabled={!canManage} data-testid="inv-contributing" /></FLabel></div>
        <div className="col-span-2"><FLabel label="Root cause"><Textarea rows={2} value={v.root_cause || ""} onChange={(e) => patch("root_cause", e.target.value)} disabled={!canManage} data-testid="inv-root-cause" /></FLabel></div>
        <div className="col-span-2"><FLabel label="Findings"><Textarea rows={3} value={v.findings || ""} onChange={(e) => patch("findings", e.target.value)} disabled={!canManage} data-testid="inv-findings" /></FLabel></div>
        <div className="col-span-2"><FLabel label="Evidence"><Textarea rows={2} value={v.evidence || ""} onChange={(e) => patch("evidence", e.target.value)} disabled={!canManage} data-testid="inv-evidence" placeholder="Reference statements, footage, records. Attach files below." /></FLabel></div>
        <div className="col-span-2"><FLabel label="Lessons identified"><Textarea rows={2} value={v.lessons_identified || ""} onChange={(e) => patch("lessons_identified", e.target.value)} disabled={!canManage} data-testid="inv-lessons" /></FLabel></div>
      </div>
      {canManage && (
        <div className="flex justify-end mt-4">
          <Button onClick={save} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-save-investigation"><Save className="w-3.5 h-3.5 mr-1.5" /> Save Investigation</Button>
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------
function ActionsTab({ inc, canManage, onSaved }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ action: "", assigned_to: "", assigned_to_email: "", due_date: "", priority: "medium", status: "not_started", completion_date: "", completion_notes: "" });

  const reset = () => setForm({ action: "", assigned_to: "", assigned_to_email: "", due_date: "", priority: "medium", status: "not_started", completion_date: "", completion_notes: "" });

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({ action: a.action || "", assigned_to: a.assigned_to || "", assigned_to_email: a.assigned_to_email || "", due_date: a.due_date || "", priority: a.priority || "medium", status: a.status || "not_started", completion_date: a.completion_date || "", completion_notes: a.completion_notes || "" });
    setAdding(false);
  };

  const startAdd = () => { reset(); setEditing(null); setAdding(true); };

  const save = async () => {
    if (!form.action.trim()) return toast.error("Action description is required");
    try {
      if (editing) {
        await api.patch(`/incidents/${inc.id}/actions/${editing}`, form);
        toast.success("Action updated");
      } else {
        await api.post(`/incidents/${inc.id}/actions`, form);
        toast.success("Action added");
      }
      setAdding(false); setEditing(null); reset();
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (aid) => {
    if (!window.confirm("Remove this action?")) return;
    try {
      await api.delete(`/incidents/${inc.id}/actions/${aid}`);
      toast.success("Action removed");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Section title="Corrective actions" right={canManage && <Button size="sm" onClick={startAdd} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-add-action"><Plus className="w-3.5 h-3.5 mr-1" /> Add action</Button>}>
      {(inc.actions || []).length === 0 && !adding ? (
        <div className="text-sm text-slate-500 py-8 text-center">No corrective actions yet.</div>
      ) : (
        <table className="data-table" data-testid="actions-table">
          <thead>
            <tr>
              <th>Action</th><th>Assigned To</th><th>Due</th><th>Priority</th><th>Status</th><th>Completed</th><th className="w-16 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(inc.actions || []).map((a) => (
              <tr key={a.id} data-testid={`action-row-${a.id}`}>
                <td>{a.action}</td>
                <td>{a.assigned_to || "—"}</td>
                <td>{a.due_date || "—"}</td>
                <td><StatusPill value={a.priority} kind="severity" /></td>
                <td><StatusPill value={a.status} kind="generic" /></td>
                <td>{a.completion_date || "—"}</td>
                <td className="text-right whitespace-nowrap">
                  {(canManage || (a.assigned_to_email || "").toLowerCase() === (window.__ayo_user_email__ || "").toLowerCase()) && (
                    <button className="p-1 text-slate-500 hover:text-sky-700" onClick={() => startEdit(a)} data-testid={`btn-edit-action-${a.id}`} title="Edit"><ClipboardCheck className="w-3.5 h-3.5" /></button>
                  )}
                  {canManage && (
                    <button className="p-1 text-slate-500 hover:text-red-600 ml-0.5" onClick={() => del(a.id)} data-testid={`btn-delete-action-${a.id}`} title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(adding || editing) && (
        <div className="mt-4 p-4 border border-sky-200 rounded-lg bg-sky-50">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><FLabel label="Action *"><Input value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} data-testid="input-action-desc" /></FLabel></div>
            <FLabel label="Assigned to (name)"><Input value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} data-testid="input-assigned-to" /></FLabel>
            <FLabel label="Assigned to (email)"><Input value={form.assigned_to_email} onChange={(e) => setForm({ ...form, assigned_to_email: e.target.value })} data-testid="input-assigned-email" placeholder="For sign-in matching" /></FLabel>
            <FLabel label="Due date"><Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="input-due-date" /></FLabel>
            <FLabel label="Priority"><Select value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} options={ACTION_PRIORITIES} testid="input-priority" /></FLabel>
            <FLabel label="Status"><Select value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={ACTION_STATUSES} testid="input-action-status" /></FLabel>
            <FLabel label="Completion date"><Input type="date" value={form.completion_date || ""} onChange={(e) => setForm({ ...form, completion_date: e.target.value })} data-testid="input-completion-date" /></FLabel>
            <div className="col-span-2"><FLabel label="Completion notes"><Textarea rows={2} value={form.completion_notes} onChange={(e) => setForm({ ...form, completion_notes: e.target.value })} data-testid="input-completion-notes" /></FLabel></div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => { setAdding(false); setEditing(null); reset(); }} data-testid="btn-cancel-action">Cancel</Button>
            <Button onClick={save} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-save-action">{editing ? "Update Action" : "Add Action"}</Button>
          </div>
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Safeguarding
// ---------------------------------------------------------------------------
function SafeguardingTab({ inc, canManage, onSaved }) {
  const [status, setStatus] = useState(inc.safeguarding_status || "not_applicable");
  const [v, setV] = useState(inc.safeguarding || {
    referral_required: false, la_notified: false, referral_date: "", referral_reference: "",
    police_involvement: false, other_agencies: "", outcome: "",
    cqc_notification_considered: false, cqc_notification_made: false, cqc_notification_date: "", cqc_notification_reference: "",
    duty_of_candour: "not_applicable", duty_of_candour_notes: ""
  });
  const patch = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const save = async () => {
    try {
      // Save top-level status first, then details
      await api.patch(`/incidents/${inc.id}`, { safeguarding_status: status });
      await api.put(`/incidents/${inc.id}/safeguarding`, v);
      toast.success("Safeguarding recorded");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Section title="Safeguarding assessment & escalation">
      {!canManage && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">Read-only — only managers can update this section.</div>}
      <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-900">
        Ayobell records your decision-making. It does not automatically make legal or regulatory decisions on your behalf.
      </div>

      <FLabel label="Safeguarding concern identified?">
        <div className="flex gap-4 mt-1">
          {SAFEGUARDING.map((s) => (
            <label key={s} className="flex items-center gap-1.5 text-sm text-slate-800 capitalize">
              <input type="radio" name="sgstatus" value={s} checked={status === s} onChange={() => setStatus(s)} disabled={!canManage} data-testid={`sgstatus-${s}`} />
              {s.replace(/_/g, " ")}
            </label>
          ))}
        </div>
      </FLabel>

      <div className="mt-4 grid grid-cols-2 gap-4">
        <Check label="Safeguarding referral required/considered" checked={v.referral_required} onChange={(x) => patch("referral_required", x)} disabled={!canManage} testid="sg-referral-required" />
        <Check label="Local authority notified" checked={v.la_notified} onChange={(x) => patch("la_notified", x)} disabled={!canManage} testid="sg-la-notified" />
        <FLabel label="Referral date"><Input type="date" value={v.referral_date || ""} onChange={(e) => patch("referral_date", e.target.value)} disabled={!canManage} data-testid="sg-referral-date" /></FLabel>
        <FLabel label="Referral / reference number"><Input value={v.referral_reference || ""} onChange={(e) => patch("referral_reference", e.target.value)} disabled={!canManage} data-testid="sg-referral-ref" /></FLabel>
        <Check label="Police involvement" checked={v.police_involvement} onChange={(x) => patch("police_involvement", x)} disabled={!canManage} testid="sg-police" />
        <FLabel label="Other agencies notified"><Input value={v.other_agencies || ""} onChange={(e) => patch("other_agencies", e.target.value)} disabled={!canManage} data-testid="sg-other-agencies" /></FLabel>
        <div className="col-span-2"><FLabel label="Outcome"><Textarea rows={2} value={v.outcome || ""} onChange={(e) => patch("outcome", e.target.value)} disabled={!canManage} data-testid="sg-outcome" /></FLabel></div>
      </div>

      <h3 className="font-heading text-sm font-semibold text-slate-900 mt-6 mb-2">CQC notification</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Check label="CQC notification considered" checked={v.cqc_notification_considered} onChange={(x) => patch("cqc_notification_considered", x)} disabled={!canManage} testid="cqc-considered" />
        <Check label="CQC notification made" checked={v.cqc_notification_made} onChange={(x) => patch("cqc_notification_made", x)} disabled={!canManage} testid="cqc-made" />
        <FLabel label="Notification date"><Input type="date" value={v.cqc_notification_date || ""} onChange={(e) => patch("cqc_notification_date", e.target.value)} disabled={!canManage} data-testid="cqc-date" /></FLabel>
        <FLabel label="Reference"><Input value={v.cqc_notification_reference || ""} onChange={(e) => patch("cqc_notification_reference", e.target.value)} disabled={!canManage} data-testid="cqc-ref" /></FLabel>
      </div>

      <h3 className="font-heading text-sm font-semibold text-slate-900 mt-6 mb-2">Duty of Candour</h3>
      <FLabel label="Duty of Candour considered">
        <div className="flex gap-4 mt-1">
          {DOC_STATUSES.map((d) => (
            <label key={d} className="flex items-center gap-1.5 text-sm text-slate-800 capitalize">
              <input type="radio" name="doc" value={d} checked={v.duty_of_candour === d} onChange={() => patch("duty_of_candour", d)} disabled={!canManage} data-testid={`doc-${d}`} />
              {d.replace(/_/g, " ")}
            </label>
          ))}
        </div>
      </FLabel>
      <div className="mt-3"><FLabel label="Rationale / notes"><Textarea rows={3} value={v.duty_of_candour_notes || ""} onChange={(e) => patch("duty_of_candour_notes", e.target.value)} disabled={!canManage} data-testid="doc-notes" /></FLabel></div>

      {canManage && (
        <div className="flex justify-end mt-4">
          <Button onClick={save} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-save-safeguarding"><Save className="w-3.5 h-3.5 mr-1.5" /> Save Safeguarding</Button>
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Closure
// ---------------------------------------------------------------------------
function ClosureTab({ inc, canManage, highSev, onSaved }) {
  const isClosed = inc.status === "closed";
  const [followup, setFollowup] = useState(inc.care_followup || {
    risk_assessment_reviewed: false, care_plan_reviewed: false, medication_review: false,
    staff_supervision: false, staff_training: false, safeguarding_referral: false, other_action: ""
  });
  const [v, setV] = useState({
    outcome: inc.closure?.outcome || "",
    root_cause: inc.closure?.root_cause || inc.investigation?.root_cause || "",
    lessons_learned: inc.closure?.lessons_learned || inc.investigation?.lessons_identified || "",
    manager_comments: inc.closure?.manager_comments || "",
    reviewed_by: inc.closure?.reviewed_by || "",
    manager_signoff: inc.closure?.manager_signoff || "",
    actions_completed_confirmed: !!inc.closure?.actions_completed_confirmed,
    risk_assessment_reviewed: !!inc.closure?.risk_assessment_reviewed,
    care_plan_reviewed: !!inc.closure?.care_plan_reviewed,
  });
  const patch = (k, val) => setV((s) => ({ ...s, [k]: val }));

  const saveFollowup = async () => {
    try { await api.put(`/incidents/${inc.id}/care-followup`, followup); toast.success("Care follow-up saved"); onSaved?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const close = async () => {
    if (!v.outcome.trim()) return toast.error("Outcome is required");
    if (!v.manager_signoff.trim()) return toast.error("Manager sign-off is required");
    if (!window.confirm("Close this incident? This will lock further edits (a manager can reopen it later).")) return;
    try {
      await api.post(`/incidents/${inc.id}/close`, v);
      toast.success("Incident closed");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const pendingActions = (inc.actions || []).filter((a) => a.status !== "completed");

  return (
    <>
      {/* Care follow-up */}
      <Section title="Care record follow-up">
        {!canManage && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">Read-only — only managers can update this section.</div>}
        <div className="grid grid-cols-2 gap-3">
          <Check label="Risk Assessment reviewed / updated" checked={followup.risk_assessment_reviewed} onChange={(x) => setFollowup({ ...followup, risk_assessment_reviewed: x })} disabled={!canManage} testid="cf-risk" />
          <Check label="Care Plan reviewed / updated" checked={followup.care_plan_reviewed} onChange={(x) => setFollowup({ ...followup, care_plan_reviewed: x })} disabled={!canManage} testid="cf-careplan" />
          <Check label="Medication review" checked={followup.medication_review} onChange={(x) => setFollowup({ ...followup, medication_review: x })} disabled={!canManage} testid="cf-meds" />
          <Check label="Staff supervision" checked={followup.staff_supervision} onChange={(x) => setFollowup({ ...followup, staff_supervision: x })} disabled={!canManage} testid="cf-supervision" />
          <Check label="Staff training" checked={followup.staff_training} onChange={(x) => setFollowup({ ...followup, staff_training: x })} disabled={!canManage} testid="cf-training" />
          <Check label="Safeguarding referral" checked={followup.safeguarding_referral} onChange={(x) => setFollowup({ ...followup, safeguarding_referral: x })} disabled={!canManage} testid="cf-safeguarding" />
        </div>
        <div className="mt-3"><FLabel label="Other corrective action"><Textarea rows={2} value={followup.other_action || ""} onChange={(e) => setFollowup({ ...followup, other_action: e.target.value })} disabled={!canManage} data-testid="cf-other" /></FLabel></div>
        {canManage && (
          <div className="flex justify-end mt-3">
            <Button size="sm" variant="outline" onClick={saveFollowup} data-testid="btn-save-followup"><Save className="w-3.5 h-3.5 mr-1.5" /> Save follow-up</Button>
          </div>
        )}
      </Section>

      {/* Closure */}
      <div className="mt-6">
      <Section title={`Review & closure${highSev ? " (high severity)" : ""}`}>
        {!canManage && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">Read-only — only managers can close incidents.</div>}
        {isClosed && (
          <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Closed on {inc.closure?.closed_at} by {inc.closure?.closed_by_name || inc.closure?.closed_by}
          </div>
        )}
        {highSev && pendingActions.length > 0 && !isClosed && (
          <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2 mb-3">
            <strong>{pendingActions.length}</strong> corrective action(s) still open — you cannot close a high-severity incident with outstanding actions.
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><FLabel label="Outcome *"><Textarea rows={2} value={v.outcome} onChange={(e) => patch("outcome", e.target.value)} disabled={!canManage || isClosed} data-testid="cl-outcome" /></FLabel></div>
          <div className="col-span-2"><FLabel label="Root cause"><Textarea rows={2} value={v.root_cause} onChange={(e) => patch("root_cause", e.target.value)} disabled={!canManage || isClosed} data-testid="cl-root-cause" /></FLabel></div>
          <div className="col-span-2"><FLabel label={highSev ? "Lessons learned *" : "Lessons learned"}><Textarea rows={2} value={v.lessons_learned} onChange={(e) => patch("lessons_learned", e.target.value)} disabled={!canManage || isClosed} data-testid="cl-lessons" /></FLabel></div>
          <div className="col-span-2"><FLabel label="Manager comments"><Textarea rows={2} value={v.manager_comments} onChange={(e) => patch("manager_comments", e.target.value)} disabled={!canManage || isClosed} data-testid="cl-comments" /></FLabel></div>
          <FLabel label="Reviewed by"><Input value={v.reviewed_by} onChange={(e) => patch("reviewed_by", e.target.value)} disabled={!canManage || isClosed} data-testid="cl-reviewed-by" /></FLabel>
          <FLabel label="Manager sign-off *"><Input value={v.manager_signoff} onChange={(e) => patch("manager_signoff", e.target.value)} disabled={!canManage || isClosed} placeholder="Type your full name" data-testid="cl-signoff" /></FLabel>
          <Check label="Actions completed" checked={v.actions_completed_confirmed} onChange={(x) => patch("actions_completed_confirmed", x)} disabled={!canManage || isClosed} testid="cl-actions-done" />
          <Check label="Risk assessment reviewed" checked={v.risk_assessment_reviewed} onChange={(x) => patch("risk_assessment_reviewed", x)} disabled={!canManage || isClosed} testid="cl-ra-reviewed" />
          <Check label="Care plan reviewed" checked={v.care_plan_reviewed} onChange={(x) => patch("care_plan_reviewed", x)} disabled={!canManage || isClosed} testid="cl-cp-reviewed" />
        </div>
        {canManage && !isClosed && (
          <div className="flex justify-end mt-4">
            <Button onClick={close} className="bg-red-700 hover:bg-red-800" data-testid="btn-close-incident"><Lock className="w-3.5 h-3.5 mr-1.5" /> Close Incident</Button>
          </div>
        )}
      </Section>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------
function AuditTab({ events }) {
  return (
    <Section title="Audit trail" right={<span className="text-xs text-slate-500">{events.length} event(s)</span>}>
      {events.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center">No audit events yet.</div>
      ) : (
        <ol className="relative border-l-2 border-slate-200 ml-3 space-y-3" data-testid="audit-timeline">
          {events.map((e) => (
            <li key={e.id} className="pl-4 pb-2">
              <div className="absolute -left-[7px] w-3 h-3 rounded-full bg-sky-500 border-2 border-white" />
              <div className="text-xs text-slate-500">{fmt(e.changed_at)}</div>
              <div className="text-sm text-slate-900 font-medium capitalize">{e.action} — {e.summary}</div>
              <div className="text-[11px] text-slate-500">{e.actor_name} ({e.actor_role})</div>
              {e.diff && e.diff.length > 0 && (
                <div className="mt-1 text-[11px] text-slate-600 space-y-0.5">
                  {e.diff.map((d, i) => (
                    <div key={i}><span className="font-mono">{d.field}</span>: {String(d.from ?? "—")} → {String(d.to ?? "—")}</div>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------
function Section({ title, right, children }) {
  return (
    <section className="bg-white rounded-lg border border-slate-200 p-6 mb-3">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-base font-semibold text-slate-900">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function FLabel({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

function ROrE({ edit, label, value, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-800 mt-0.5">{edit && children ? children : (value || "—")}</div>
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2 capitalize">
      {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
    </select>
  );
}

function Check({ label, checked, onChange, disabled, testid }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-800">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} data-testid={testid} />
      {label}
    </label>
  );
}
